import {
  assignCommercialAgentAPI,
  createCommercialAgentAPI,
  deleteCommercialAgentAPI,
  getCommercialAgentsAPI,
  unassignCommercialAgentAPI,
  updateCommercialAgentAPI,
} from 'api/endpoints/commercialAgent/commercialAgent.api';
import { TcommercialAgentDispatchReducer } from './commercialAgent.reducer';
import * as CommercialAgentType from 'contexts/commercialAgent/reducer/commercialAgent.types';
import { ITranslate } from 'interfaces/general.interface';
import { notificationErr } from 'views/components/UI/notification';
import { commercialAgentsError } from 'utils/errorCodes';
import {
  ICommercialAgentClientAliasesItem,
  IcreateCommercialAgentAPIBody,
  IgetCommercialAgentsAPIResponseAPIData,
} from 'api/endpoints/commercialAgent/commercialAgent.response.interface';
import { messageError, messageSuccess } from 'views/components/UI/message';

export async function getCommercialAgentsAction({
  dispatch,
  tenantId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcommercialAgentDispatchReducer>;
  tenantId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({
    type: CommercialAgentType.SET_IS_LOADING_GET_COMMERCIAL_AGENTS,
    payload: true,
  });
  try {
    const getCommercialAgents = await getCommercialAgentsAPI({
      tenantId,
      token,
    });
    if (getCommercialAgents.response.status === 200) {
      dispatch({
        type: CommercialAgentType.SET_COMMERCIAL_AGENTS,
        payload: getCommercialAgents.data.sort((a, b) =>
          a.createdAt < b.createdAt ? 1 : -1
        ),
      });
      return true;
    }
    if (getCommercialAgents.response.status !== 404) {
      messageError(translate('actions_commercial-agents_get-commercial-agents_error'));
      notificationErr(commercialAgentsError.getCommercialAgents_002, translate);
    }
    return false;
  } catch (error) {
    messageError(translate('actions_commercial-agents_get-commercial-agents_error'));
    notificationErr(commercialAgentsError.getCommercialAgents_001, translate);
    return false;
  } finally {
    dispatch({
      type: CommercialAgentType.SET_IS_LOADING_GET_COMMERCIAL_AGENTS,
      payload: false,
    });
  }
}

export async function selectAgentAction({
  dispatch,
  agent,
}: {
  dispatch: React.Dispatch<TcommercialAgentDispatchReducer>;
  agent: IgetCommercialAgentsAPIResponseAPIData | null;
}): Promise<void> {
  dispatch({
    type: CommercialAgentType.SET_AGENT_SELECTED,
    payload: agent,
  });
}

export async function createCommercialAgentAction({
  dispatch,
  agent,
  tenantId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcommercialAgentDispatchReducer>;
  agent: IcreateCommercialAgentAPIBody;
  tenantId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({
    type: CommercialAgentType.SET_IS_LOADING_CREATE_UPDATE_COMMERCIAL_AGENT,
    payload: true,
  });
  const clientsToAssign: string[] | undefined = agent.clients_selected_idx;
  delete agent.clients_selected_idx;
  try {
    const createCommercialAgent = await createCommercialAgentAPI({
      tenantId,
      agent,
      token,
    });
    if (createCommercialAgent.response.status === 200) {
      if (
        clientsToAssign &&
        clientsToAssign.length > 0 &&
        createCommercialAgent.data.agent.id
      ) {
        for (const client_id of clientsToAssign) {
          try {
            const assignClientFetch = await assignCommercialAgentAPI({
              commercialAgentId: createCommercialAgent.data.agent.id,
              clientAliasId: client_id,
              token,
            });
            if (assignClientFetch.response.status !== 200) {
              messageError(
                translate('actions_commercial-agents_assign-commercial-agent_error')
              );
              notificationErr(commercialAgentsError.createCommercialAgent_004, translate);
            }
          } catch (error) {
            messageError(
              translate('actions_commercial-agents_assign-commercial-agent_error')
            );
            notificationErr(commercialAgentsError.createCommercialAgent_003, translate);
          }
        }
      }
      messageSuccess(
        translate('actions_commercial-agents_create-commercial-agent_success')
      );
      return await getCommercialAgentsAction({
        dispatch,
        tenantId,
        token,
        translate,
      });
    }
    messageError(translate('actions_commercial-agents_create-commercial-agent_error'));
    notificationErr(commercialAgentsError.createCommercialAgent_002, translate);
    return false;
  } catch (error) {
    messageError(translate('actions_commercial-agents_create-commercial-agent_error'));
    notificationErr(commercialAgentsError.createCommercialAgent_001, translate);
    return false;
  } finally {
    dispatch({
      type: CommercialAgentType.SET_IS_LOADING_CREATE_UPDATE_COMMERCIAL_AGENT,
      payload: false,
    });
  }
}

export async function updateCommercialAgentAction({
  dispatch,
  tenantId,
  clientsAliasesAssigned,
  agentId,
  agent,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcommercialAgentDispatchReducer>;
  tenantId: string;
  clientsAliasesAssigned: Array<ICommercialAgentClientAliasesItem>;
  agentId: string;
  agent: Partial<IcreateCommercialAgentAPIBody>;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({
    type: CommercialAgentType.SET_IS_LOADING_CREATE_UPDATE_COMMERCIAL_AGENT,
    payload: true,
  });
  try {
    const updateCommercialAgent = await updateCommercialAgentAPI({
      commercialAgentId: agentId,
      agent,
      token,
    });
    if (updateCommercialAgent.response.status === 200) {
      let clientsToUnassign: string[] = [];
      let clientsToAssign: string[] = [];
      let clientsUnassignedUpdated: number = 0;
      let clientsAssignedUpdated: number = 0;

      if (!agent.clients_selected_idx || agent.clients_selected_idx.length === 0) {
        clientsToUnassign = clientsAliasesAssigned.map((client) => client.id);
      }

      if (
        clientsAliasesAssigned.length === 0 &&
        agent.clients_selected_idx &&
        agent.clients_selected_idx.length > 0
      ) {
        clientsToAssign = agent.clients_selected_idx || [];
      }

      if (
        clientsAliasesAssigned.length > 0 &&
        agent.clients_selected_idx &&
        agent.clients_selected_idx.length > 0
      ) {
        const clientsAssignedIds = clientsAliasesAssigned.map(
          (client) => client.client_alias.id
        );
        for (const clientId of agent.clients_selected_idx) {
          if (!clientsAssignedIds.includes(clientId)) {
            clientsToAssign.push(clientId);
          }
        }
        for (const client of clientsAliasesAssigned) {
          if (!agent.clients_selected_idx.includes(client.client_alias.id)) {
            clientsToUnassign.push(client.id);
          }
        }
      }

      if (clientsToUnassign.length >= 1) {
        for (const client_id of clientsToUnassign) {
          try {
            const assignClientFetch = await unassignCommercialAgentAPI({
              comercialAgentClientAliasesId: client_id,
              token,
            });
            if (assignClientFetch.response.status === 200) {
              clientsUnassignedUpdated++;
            } else {
              messageError(
                translate('actions_commercial-agents_unassign-commercial-agent_error')
              );
              notificationErr(commercialAgentsError.updateCommercialAgent_006, translate);
            }
          } catch (error) {
            messageError(
              translate('actions_commercial-agents_unassign-commercial-agent_error')
            );
            notificationErr(commercialAgentsError.updateCommercialAgent_005, translate);
          }
        }
      }
      if (clientsToAssign.length >= 1) {
        for (const client_id of clientsToAssign) {
          try {
            const assignClientFetch = await assignCommercialAgentAPI({
              commercialAgentId: agentId,
              clientAliasId: client_id,
              token,
            });
            if (assignClientFetch.response.status === 200) {
              clientsAssignedUpdated++;
            } else {
              messageError(
                translate('actions_commercial-agents_assign-commercial-agent_error')
              );
              notificationErr(commercialAgentsError.updateCommercialAgent_004, translate);
            }
          } catch (error) {
            messageError(
              translate('actions_commercial-agents_assign-commercial-agent_error')
            );
            notificationErr(commercialAgentsError.updateCommercialAgent_003, translate);
          }
        }
      }
      if (clientsAssignedUpdated === 0 && clientsUnassignedUpdated === 0) {
        dispatch({
          type: CommercialAgentType.SET_UPDATED_COMMERCIAL_AGENT,
          payload: {
            agentId,
            agent,
          },
        });
        messageSuccess(
          translate('actions_commercial-agents_update-commercial-agent_success')
        );
        return true;
      } else {
        messageSuccess(
          translate('actions_commercial-agents_update-commercial-agent_success')
        );
        return await getCommercialAgentsAction({
          dispatch,
          tenantId,
          token,
          translate,
        });
      }
    }
    messageError(translate('actions_commercial-agents_update-commercial-agent_error'));
    notificationErr(commercialAgentsError.updateCommercialAgent_002, translate);
    return false;
  } catch (error) {
    messageError(translate('actions_commercial-agents_update-commercial-agent_error'));
    notificationErr(commercialAgentsError.updateCommercialAgent_001, translate);
    return false;
  } finally {
    dispatch({
      type: CommercialAgentType.SET_IS_LOADING_CREATE_UPDATE_COMMERCIAL_AGENT,
      payload: false,
    });
  }
}

export async function deleteCommercialAgentAction({
  dispatch,
  agentId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcommercialAgentDispatchReducer>;
  agentId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({
    type: CommercialAgentType.SET_IS_LOADING_DELETE_COMMERCIAL_AGENT,
    payload: {
      isLoading: true,
      agentId,
    },
  });
  try {
    const deleteCommercialAgent = await deleteCommercialAgentAPI({
      commercialAgentId: agentId,
      token,
    });
    if (deleteCommercialAgent.response.status === 200) {
      messageSuccess(
        translate('actions_commercial-agents_delete-commercial-agent_success')
      );
      dispatch({
        type: CommercialAgentType.DELETE_COMMERCIAL_AGENT,
        payload: agentId,
      });
      return true;
    }
    messageError(translate('actions_commercial-agents_delete-commercial-agent_error'));
    notificationErr(commercialAgentsError.deleteCommercialAgent_002, translate);
    return false;
  } catch (error) {
    messageError(translate('actions_commercial-agents_delete-commercial-agent_error'));
    notificationErr(commercialAgentsError.deleteCommercialAgent_001, translate);
    return false;
  } finally {
    dispatch({
      type: CommercialAgentType.SET_IS_LOADING_DELETE_COMMERCIAL_AGENT,
      payload: {
        isLoading: false,
        agentId: null,
      },
    });
  }
}

export async function assignAgentsToClientAction({
  dispatch,
  tenantId,
  agentsToAddIdx,
  agentsOnClient,
  clientId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TcommercialAgentDispatchReducer>;
  tenantId: string;
  agentsToAddIdx: Array<string>;
  agentsOnClient: IgetCommercialAgentsAPIResponseAPIData[];
  clientId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({
    type: CommercialAgentType.SET_IS_LOADING_ASSIGN_AGENTS_TO_CLIENT,
    payload: true,
  });

  let agentsToUnassign: string[] = [];
  let agentsToAssign: string[] = [];
  let agentsUnassignedUpdated: number = 0;
  let agentsAssignedUpdated: number = 0;

  for (const agentId of agentsToAddIdx) {
    if (!agentsOnClient.map((agent) => agent.id).includes(agentId)) {
      agentsToAssign.push(agentId);
    }
  }

  for (const agent of agentsOnClient) {
    if (!agentsToAddIdx.includes(agent.id)) {
      const findClientAliases = agent.comercial_agent_client_aliases.find(
        (client) => client.client_alias.id === clientId
      );
      findClientAliases && agentsToUnassign.push(findClientAliases.id);
    }
  }

  try {
    if (agentsToUnassign.length >= 1) {
      for (const clientAliasId of agentsToUnassign) {
        try {
          const assignClientFetch = await unassignCommercialAgentAPI({
            comercialAgentClientAliasesId: clientAliasId,
            token,
          });
          if (assignClientFetch.response.status === 200) {
            agentsUnassignedUpdated++;
          } else {
            messageError(
              translate('actions_commercial-agents_unassign-commercial-agent_error')
            );
            notificationErr(commercialAgentsError.updateCommercialAgent_006, translate);
          }
        } catch (error) {
          messageError(
            translate('actions_commercial-agents_unassign-commercial-agent_error')
          );
          notificationErr(commercialAgentsError.updateCommercialAgent_005, translate);
        }
      }
    }
    if (agentsToAssign.length >= 1) {
      for (const agentId of agentsToAssign) {
        try {
          const assignClientFetch = await assignCommercialAgentAPI({
            commercialAgentId: agentId,
            clientAliasId: clientId,
            token,
          });
          if (assignClientFetch.response.status === 200) {
            agentsAssignedUpdated++;
          } else {
            messageError(
              translate('actions_commercial-agents_assign-commercial-agent_error')
            );
            notificationErr(commercialAgentsError.updateCommercialAgent_004, translate);
          }
        } catch (error) {
          messageError(
            translate('actions_commercial-agents_assign-commercial-agent_error')
          );
          notificationErr(commercialAgentsError.updateCommercialAgent_003, translate);
        }
      }
    }

    if (agentsAssignedUpdated > 0 || agentsUnassignedUpdated > 0) {
      return await getCommercialAgentsAction({
        dispatch,
        tenantId,
        token,
        translate,
      });
    }

    return true;
  } catch (error) {
    messageError(translate('actions_commercial-agents_assign-commercial-agent_error'));
    notificationErr(commercialAgentsError.assignAgentsToClient_001, translate);
    return false;
  } finally {
    dispatch({
      type: CommercialAgentType.SET_IS_LOADING_ASSIGN_AGENTS_TO_CLIENT,
      payload: false,
    });
  }
}
